import {
  User,
  onAuthStateChanged,
  signInWithPopup,
  signOut as firebaseSignOut,
} from "firebase/auth";
import { FirebaseError } from "firebase/app";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createConfig } from "../Config";
import {
  firebaseAuth,
  googleAuthProvider,
  isFirebaseConfigured,
} from "../firebase";
import { bootstrapUser as bootstrapUserRequest } from "../api/generated/sdk.gen";
import type { Auth } from "../api/generated/core/auth.gen";

type AuthState = "initializing" | "authenticated" | "unauthenticated";

type BootstrapResult = {
  userId: string;
  source: string;
  created: boolean;
};

type AuthContextValue = {
  state: AuthState;
  firebaseUser: User | null;
  userId: string | null;
  errorMessage: string | null;
  getIdToken: () => Promise<string | null>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

const config = createConfig();

class UnlinkedGoogleAccountError extends Error {
  constructor() {
    super("GoogleアカウントがShiftasに連携されていません。");
  }
}

function firebaseBearerAuth(token: string) {
  return (auth: Auth) => {
    // server の private API 認証は「旧APIキーまたはFirebase Bearer」のOR運用。
    // 生成SDKは両方のsecurity定義を順に処理するため、WebログインではBearerだけを送る。
    return auth.scheme === "bearer" ? token : undefined;
  };
}

async function bootstrapUser(firebaseUser: User): Promise<BootstrapResult> {
  const token = await firebaseUser.getIdToken();

  const response = await bootstrapUserRequest({
    baseUrl: config.apiUrl,
    auth: firebaseBearerAuth(token),
    body: {
      createIfMissing: false,
      deviceName: "Web",
      platform: "web",
    },
    throwOnError: false,
  });

  if (response.response?.status === 404) {
    throw new UnlinkedGoogleAccountError();
  }

  if (response.error || !response.data) {
    throw new Error("ログイン情報の確認に失敗しました。");
  }

  return response.data;
}

function signInErrorMessage(error: unknown) {
  if (error instanceof FirebaseError && error.code === "auth/unauthorized-domain") {
    return "このドメインはGoogleログインに許可されていません。Firebase Authenticationの承認済みドメインに追加してください。";
  }

  return error instanceof Error ? error.message : "Googleログインに失敗しました。";
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [state, setState] = useState<AuthState>("initializing");
  const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const authRequestIdRef = useRef(0);

  const applyFirebaseUser = useCallback(async (user: User | null) => {
    const requestId = ++authRequestIdRef.current;
    if (!user) {
      setFirebaseUser(null);
      setUserId(null);
      setState("unauthenticated");
      return;
    }

    setErrorMessage(null);

    try {
      const bootstrap = await bootstrapUser(user);
      if (requestId !== authRequestIdRef.current) return;
      setFirebaseUser(user);
      setUserId(bootstrap.userId);
      setState("authenticated");
    } catch (error) {
      if (requestId !== authRequestIdRef.current) return;
      setFirebaseUser(null);
      setUserId(null);
      setState("unauthenticated");
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "ログイン情報の確認に失敗しました。"
      );
      if (firebaseAuth && error instanceof UnlinkedGoogleAccountError) {
        await firebaseSignOut(firebaseAuth);
      }
    }
  }, []);

  useEffect(() => {
    if (!firebaseAuth || !isFirebaseConfigured) {
      setState("unauthenticated");
      setErrorMessage("Googleログイン設定が未完了です。");
      return;
    }

    return onAuthStateChanged(firebaseAuth, (user) => {
      void applyFirebaseUser(user);
    });
  }, [applyFirebaseUser]);

  const getIdToken = useCallback(async () => {
    const user = firebaseAuth?.currentUser ?? firebaseUser;
    return user ? user.getIdToken() : null;
  }, [firebaseUser]);

  const signInWithGoogle = useCallback(async () => {
    if (!firebaseAuth) {
      setErrorMessage("Googleログイン設定が未完了です。");
      return;
    }

    authRequestIdRef.current += 1;
    setState("initializing");
    setErrorMessage(null);
    try {
      await signInWithPopup(firebaseAuth, googleAuthProvider);
    } catch (error) {
      setState("unauthenticated");
      setErrorMessage(signInErrorMessage(error));
    }
  }, [applyFirebaseUser]);

  const signOut = useCallback(async () => {
    authRequestIdRef.current += 1;
    if (firebaseAuth) {
      await firebaseSignOut(firebaseAuth);
    }
    setErrorMessage(null);
    setFirebaseUser(null);
    setUserId(null);
    setState("unauthenticated");
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      state,
      firebaseUser,
      userId,
      errorMessage,
      getIdToken,
      signInWithGoogle,
      signOut,
    }),
    [
      errorMessage,
      firebaseUser,
      getIdToken,
      signInWithGoogle,
      signOut,
      state,
      userId,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) {
    throw new Error("useAuth must be used inside AuthProvider");
  }
  return value;
}
